import type { SingleBillResponse } from '../../repositories';

export class SingleBill {
  private readonly customerInfo: NonNullable<SingleBillResponse['billing_information']>['customer'] | null;

  private readonly paymentInfo: NonNullable<SingleBillResponse['billing_information']>['payment_method'] | null;

  private readonly partnerContact: NonNullable<SingleBillResponse['billing_information']>['partner_contact'] | null;

  private readonly singleBillEnabled: boolean;

  private readonly singleBillActivated: boolean;

  private readonly accountBalance: number | null;

  private readonly accountBalanceDue: number | null;

  private readonly nextBillingDate: string | null;

  private readonly currencyCode: string | null;

  private readonly locale: string | null;

  private readonly payingAccountId: number | null;

  constructor(billingInfoApi: SingleBillResponse) {
    this.customerInfo = billingInfoApi.billing_information?.customer ?? null;
    this.paymentInfo = billingInfoApi.billing_information?.payment_method ?? null;
    this.partnerContact = billingInfoApi.billing_information?.partner_contact ?? null;
    this.singleBillEnabled = billingInfoApi.single_bill_enabled ?? null;
    this.singleBillActivated = billingInfoApi.single_bill_activated ?? null;
    this.accountBalance = billingInfoApi.account_balance ?? null;
    this.accountBalanceDue = billingInfoApi.account_balance_due ?? null;
    this.nextBillingDate = billingInfoApi.next_billing_date ?? null;
    this.currencyCode = billingInfoApi.currency_code ?? null;
    this.locale = billingInfoApi.locale ?? null;
    this.payingAccountId = billingInfoApi.paying_account_id ?? null;
  }

  // Customer Info Getters
  public getFirstName() {
    return this.customerInfo?.firstname ?? '';
  }

  public getLastName() {
    return this.customerInfo?.lastname ?? '';
  }

  public getAddress1() {
    return this.customerInfo?.address1 ?? '';
  }

  public getAddress2() {
    return this.customerInfo?.address2 ?? '';
  }

  public getCompanyName() {
    return this.customerInfo?.companyname ?? '';
  }

  public getCity() {
    return this.customerInfo?.city ?? '';
  }

  public getState() {
    return this.customerInfo?.state ?? '';
  }

  public getCountry() {
    return this.customerInfo?.country ?? '';
  }

  public getZipCode() {
    return this.customerInfo?.zipcode ?? '';
  }

  public getEmail() {
    return this.customerInfo?.email ?? '';
  }

  public getPhone() {
    return this.customerInfo?.phone ?? '';
  }

  public getBillingLetterOption() {
    return this.customerInfo?.billingLetterOption ?? 'Y';
  }

  public getCustomerId() {
    return this.customerInfo?.customerId ?? null;
  }

  public getUserUuid() {
    return this.customerInfo?.userUuid ?? null;
  }

  public getUsername() {
    return this.customerInfo?.username ?? null;
  }

  public getCountryLong() {
    return this.customerInfo?.countrylong ?? '';
  }

  public getContactEmail() {
    return this.customerInfo?.contactEmail ?? null;
  }

  public getVatNumber() {
    return this.customerInfo?.vatNumber ?? null;
  }

  public getBillingType() {
    return this.customerInfo?.billingType ?? null;
  }

  public getCustomerInSingleBilling() {
    return this.customerInfo?.customerInSingleBilling ?? false;
  }

  public getCurrencyCd() {
    return this.customerInfo?.currencyCd ?? '';
  }

  public getBillingPeriod() {
    return this.customerInfo?.billingPeriod ?? null;
  }

  public getBillingDayOfMonth() {
    return this.customerInfo?.billingDayOfMonth ?? null;
  }

  public getAddressValidationRequired() {
    return this.customerInfo?.addressValidationRequired ?? false;
  }

  public getNoPrepayAutoRenewOptin() {
    return this.customerInfo?.noPrepayAutoRenewOptin ?? 0;
  }

  public getAddressProvided() {
    return this.customerInfo?.addressProvided ?? false;
  }

  public getIsSingleBilledAccount() {
    return this.customerInfo?.singleBilledAccount ?? false;
  }

  public getIsPartnerBilledAccount() {
    return this.customerInfo?.partnerBilledAccount ?? false;
  }

  // Payment Info Getters
  public getPaymentType() {
    return this.paymentInfo?.paymentType ?? null;
  }

  public getEcheckInfo() {
    return this.paymentInfo?.echeck ?? null;
  }

  public getCreditCardInfo() {
    if (this.paymentInfo?.creditCard) {
      return this.paymentInfo.creditCard;
    }

    // Stripe-backed card should still be treated as an existing credit-card method
    if (this.paymentInfo?.paymentType?.name === 'STRIPE' && this.paymentInfo?.stripe?.type === 'card') {
      return {
        cardType: {
          code: this.paymentInfo.stripe.cardType ?? 'UNKNOWN',
          displayString: this.paymentInfo.stripe.cardType ?? 'card',
          masterCard: false,
          visa: false,
          diners: false,
          amex: false,
          discover: false,
        },
        cardNumber: this.paymentInfo.stripe.cardLast4Digits ?? '****',
        cardExpirationDate: this.paymentInfo.stripe.cardExpirationDate ?? '',
        cardSecureCode: null,
        cardHolderName: null,
        validateCardSecureCode: false,
        cardToken: this.paymentInfo.stripe.token,
        data: null,
        tokenized: true,
        expired: false,
      };
    }

    return null;
  }

  // Partner Contact Getters
  public getPartnerContactName() {
    return this.partnerContact?.partner_contact_name ?? '';
  }

  public getPartnerContactEmail() {
    return this.partnerContact?.partner_contact_email ?? '';
  }

  // Single Bill Info Getters
  public getIsSingleBillEnabled() {
    return this.singleBillEnabled;
  }

  public getIsSingleBillActivated() {
    return this.singleBillActivated;
  }

  public getAccountBalance() {
    return this.accountBalance;
  }

  public getAccountBalanceDue() {
    return this.accountBalanceDue;
  }

  public getNextBillingDate() {
    return this.nextBillingDate;
  }

  public getCurrencyCode() {
    return this.currencyCode;
  }

  public getLocale() {
    return this.locale;
  }

  public getPayingAccountId() {
    return this.payingAccountId;
  }
}
